// Intercom utility functions following official documentation patterns
// https://developers.intercom.com/installing-intercom/docs/intercom-javascript

export interface IntercomUser {
  user_id: string;
  user_hash?: string | null;
  email?: string | null;
  name?: string | null;
  realm?: string | null;
  userType?: string | null;
  numberOfSchools?: number;
  numberOfClasses?: number;
  schoolLicenses?: string;
  hasHadLicense?: boolean;
  studentsPerClass?: string;
  realmAccess?: string;
  intercomBotEnabled?: boolean;
  hardwarePreferences?: string;
  [key: string]: any;
}

export interface IntercomEvent {
  eventName: string;
  metadata?: Record<string, any>;
}

// Intercom app IDs for different environments
const INTERCOM_APP_IDS = {
  production: 'x7tavu6w',
  staging: 'x7tavu6w',
  development: 'c8a0h0kb',
  test: 'c8a0h0kb',
} as const;

// Get the appropriate app ID based on environment
export const getIntercomAppId = (): string => {
  if (typeof window === 'undefined') return INTERCOM_APP_IDS.development;

  const env = window.PT_ENV;
  if (env === 'prod') return INTERCOM_APP_IDS.production;
  if (env === 'staging') return INTERCOM_APP_IDS.staging;
  if (env === 'test') return INTERCOM_APP_IDS.test;
  return INTERCOM_APP_IDS.development;
};

// Check if Intercom is available
export const isIntercomAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.Intercom === 'function';
};

export const bootIntercom = (): void => {
  if (!isIntercomAvailable()) {
    console.warn('Intercom is not available');
    return;
  }

  try {
    window.Intercom('boot', {
      app_id: getIntercomAppId(),
    });
  } catch (error) {
    console.error('Failed to boot Intercom:', error);
  }
};

// Update user data in Intercom
export const updateIntercomUser = (userData: IntercomUser): void => {
  if (!isIntercomAvailable()) {
    console.warn('Intercom is not available');
    return;
  }

  try {
    window.Intercom('update', userData);
  } catch (error) {
    console.error('Failed to update Intercom user:', error);
  }
};

// Track an event in Intercom
export const trackIntercomEvent = (
  eventName: string,
  metadata?: Record<string, any>
): void => {
  if (!isIntercomAvailable()) {
    console.warn('Intercom is not available');
    return;
  }

  try {
    window.Intercom('trackEvent', eventName, metadata);
  } catch (error) {
    console.error('Failed to track Intercom event:', error);
  }
};

// Shutdown Intercom (useful for logout)
export const shutdownIntercom = (): void => {
  if (!isIntercomAvailable()) {
    return;
  }

  try {
    window.Intercom('shutdown');
  } catch (error) {
    console.error('Failed to shutdown Intercom:', error);
  }
};

export const resetIntercom = (): void => {
  shutdownIntercom();
  bootIntercom();
};

// Show Intercom messenger
export const showIntercom = (): void => {
  if (!isIntercomAvailable()) {
    console.warn('Intercom is not available');
    return;
  }

  try {
    window.Intercom('show');
  } catch (error) {
    console.error('Failed to show Intercom:', error);
  }
};

// Hide Intercom messenger
export const hideIntercom = (): void => {
  if (!isIntercomAvailable()) {
    console.warn('Intercom is not available');
    return;
  }

  try {
    window.Intercom('hide');
  } catch (error) {
    console.error('Failed to hide Intercom:', error);
  }
};

// Start a new conversation
export const startIntercomConversation = (message?: string): void => {
  if (!isIntercomAvailable()) {
    console.warn('Intercom is not available');
    return;
  }

  try {
    if (message) {
      window.Intercom('showNewMessage', message);
    } else {
      window.Intercom('showNewMessage');
    }
  } catch (error) {
    console.error('Failed to start Intercom conversation:', error);
  }
};
